'use client'
import React, { ReactElement, useState } from 'react'
import { HotelRating } from '@/components/hotel/hotel-rating/HotelRating'
import HotelSelect from '@/components/hotel/HotelSelect'
import { HotelRatingsData } from '@/types/Misc'
import { fetchHotelRatingsData } from '@/lib/frontent-api'
import { useTranslations } from 'next-intl'
import { Domain } from 'domain-config/domain-config'
import FilterIcon from '@/icons/font-awesome/sharp-light/filter.svg'
import BarsSortIcon from '@/icons/font-awesome/sharp-light/bars-sort.svg'
import SquareXIcon from '@/icons/font-awesome/sharp-light/square-x.svg'

import classNames from 'classnames'

export function HotelRatingList(props: {
  hotelCode: string
  domain: Domain
  hotelRatingsData: HotelRatingsData
}): ReactElement | null {
  const t = useTranslations('Hotel.HotelRatings.HotelRatingList')
  const [sortingOption, setSortingOption] = useState('2')
  const [travelerType, setTravelerType] = useState('0')
  const [page, setPage] = useState(1)
  const [hotelRatingsData, setHotelRatingsData] = useState<HotelRatingsData>(props.hotelRatingsData)
  function handleSortingOptionChange(value: string): void {
    setSortingOption(value)
    setPage(1)
    loadRatingsData(1, value, travelerType)
  }

  function handleTravellerOptionChange(value: string): void {
    setTravelerType(value)
    setPage(1)
    loadRatingsData(1, sortingOption, value)
  }

  function handleFilterReset(): void {
    setSortingOption('2')
    setTravelerType('0')
    setPage(1)
    loadRatingsData(1, '2', '0')
  }

  function handleLoadMore(): void {
    const nextPage = page + 1
    setPage(nextPage)
    loadRatingsData(nextPage, sortingOption, travelerType)
  }

  const sortedOrFiltered = sortingOption !== '2' || travelerType !== '0'

  function loadRatingsData(page: number, sortingOption: string, travelerType: string) {
    fetchHotelRatingsData(props.domain, props.hotelCode, sortingOption, travelerType, page)
      .then((r) => {
        if (page === 1) {
          setHotelRatingsData(r)
        } else {
          const newRatings = r.ratings
          setHotelRatingsData((prevData) => ({
            ...prevData,
            ratings: [...prevData.ratings, ...newRatings],
          }))
        }
      })
      .catch((error) => {
        console.error('Error fetching hotel ratings data:', error)
      })
  }

  return (
    <>
      {hotelRatingsData.ratingOverall && hotelRatingsData.ratingOverall !== 0 && (
        <div>
          <div className="flex flex-col py-10">
            <div className="mb-4 flex gap-4">
              <span className="font-bold text-stone-800">
                {hotelRatingsData.numberOfRatings} {t('numberOfRatingsText')}
              </span>
              <button
                onClick={handleFilterReset}
                className={classNames(
                  'flex cursor-pointer flex-row flex-nowrap items-center gap-2 text-stone-400 hover:text-stone-800 md:order-5',
                  { hidden: !sortedOrFiltered },
                )}
              >
                <SquareXIcon className={'h-4 w-4 fill-current'} />
                {t('filterResetText')}
              </button>
            </div>

            <div className="md:grow-02 flex flex-wrap gap-2 sm:grow">
              <div className="w-80">
                <HotelSelect
                  className="w-full"
                  options={[
                    { value: '2', label: t('filterOneOptionThree'), optionAvailable: true },
                    { value: '0', label: t('filterOneOptionOne'), optionAvailable: true },
                    { value: '1', label: t('filterOneOptionTwo'), optionAvailable: true },
                  ]}
                  selectedValue={sortingOption}
                  icon={
                    <BarsSortIcon
                      className={'absolute bottom-0 left-2 top-0 m-auto h-4 w-4 fill-stone-400'}
                    />
                  }
                  onChange={handleSortingOptionChange}
                  onReset={handleFilterReset}
                  hideUnavailableOptions={false}
                />
              </div>
              <div className="w-80">
                <HotelSelect
                  className="w-full"
                  options={[
                    { value: '0', label: t('filterTwoOptionOne'), optionAvailable: true },
                    { value: '1', label: t('filterTwoOptionTwo'), optionAvailable: true },
                    { value: '2', label: t('filterTwoOptionThree'), optionAvailable: true },
                    { value: '3', label: t('filterTwoOptionFour'), optionAvailable: true },
                    { value: '4', label: t('filterTwoOptionFive'), optionAvailable: true },
                  ]}
                  selectedValue={travelerType}
                  icon={
                    <FilterIcon
                      className={'absolute bottom-0 left-2 top-0 m-auto h-4 w-4 fill-stone-400'}
                    />
                  }
                  onChange={handleTravellerOptionChange}
                  onReset={handleFilterReset}
                  hideUnavailableOptions={false}
                />
              </div>
            </div>
          </div>
          {hotelRatingsData.ratings &&
            hotelRatingsData.ratings.length > 0 &&
            hotelRatingsData.ratings.map((rating, index) => (
              <HotelRating key={index} hotelRating={rating} />
            ))}
          {hotelRatingsData.ratings &&
            hotelRatingsData.ratings.length < hotelRatingsData.numberOfRatings && (
              <div className="mx-auto flex max-w-screen-xl items-center justify-center px-5">
                <button
                  className="my-8 ml-auto mr-auto inline-block self-center rounded-md bg-primary px-4 py-4 text-md font-semibold text-white md:px-6 md:text-xl"
                  onClick={handleLoadMore}
                >
                  {t('loadMoreRatingsButtonText')}
                </button>
              </div>
            )}
        </div>
      )}
    </>
  )
}
